import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { AlertTriangle, Baby, BarChart3, Book, Brain, CheckCircle, ChevronDown, ChevronUp, ClipboardList, Download, Edit2, Eye, FileText, Filter, Globe, Home, Phone, Plus, RefreshCw, Save, School, Search, Shield, Trash2, Upload, UserCheck, Users, X } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalRuleBook = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterRole, setFilterRole] = useState('all');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRule, setExpandedRule] = useState(null);
  
  // Edit modal state
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'General',
    applies_to: 'all',
    priority: 'medium',
    order: 0,
    is_active: true,
    consequences: '',
    examples: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadRules();
  }, [navigate]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('principal_token');
      const res = await axios.get(`${BACKEND_URL}/api/principal/rule-book`, { params: { token } });
      setRules(res.data.rules || []);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Error loading rules:', error);
      toast({ title: "Error", description: "Failed to load rules", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const initializeRules = async () => {
    if (!confirm('This will add default rules to the system. Continue?')) return;
    
    try {
      const token = localStorage.getItem('principal_token');
      const res = await axios.post(`${BACKEND_URL}/api/principal/rule-book/initialize`, null, { params: { token } });
      toast({ title: "Success", description: res.data.message });
      loadRules();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to initialize rules", variant: "destructive" });
    }
  };

  const openCreateModal = () => {
    setEditingRule(null);
    setFormData({
      title: '',
      description: '',
      category: categories[0] || 'General',
      applies_to: 'all',
      priority: 'medium',
      order: rules.length + 1,
      is_active: true,
      consequences: '',
      examples: ''
    });
    setShowModal(true);
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setFormData({
      title: rule.title || '',
      description: rule.description || '',
      category: rule.category || 'General',
      applies_to: rule.applies_to || 'all',
      priority: rule.priority || 'medium',
      order: rule.order || 0,
      is_active: rule.is_active !== false,
      consequences: rule.consequences || '',
      examples: rule.examples || ''
    });
    setShowModal(true);
  };

  const saveRule = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast({ title: "Error", description: "Title and description are required", variant: "destructive" });
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('principal_token');
      const params = {
        token,
        ...formData,
        rule_id: editingRule?.id || undefined
      };
      
      await axios.post(`${BACKEND_URL}/api/principal/rule-book`, null, { params });
      toast({ title: "Success", description: editingRule ? "Rule updated" : "Rule created" });
      setShowModal(false);
      loadRules();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to save rule", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const token = localStorage.getItem('principal_token');
      await axios.delete(`${BACKEND_URL}/api/principal/rule-book/${ruleId}`, { params: { token } });
      toast({ title: "Success", description: "Rule deleted" });
      loadRules();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete rule", variant: "destructive" });
    }
  };

  const toggleActive = async (rule) => {
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/rule-book`, null, {
        params: {
          token,
          rule_id: rule.id,
          title: rule.title,
          description: rule.description,
          category: rule.category,
          applies_to: rule.applies_to,
          priority: rule.priority,
          order: rule.order,
          is_active: !rule.is_active,
          consequences: rule.consequences,
          examples: rule.examples
        }
      });
      toast({ title: "Success", description: `Rule ${rule.is_active ? 'deactivated' : 'activated'}` });
      loadRules();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update rule", variant: "destructive" });
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'observer': return <UserCheck className="w-4 h-4" />;
      case 'principal': return <School className="w-4 h-4" />;
      case 'parent': return <Baby className="w-4 h-4" />;
      default: return <Globe className="w-4 h-4" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'observer': return 'bg-purple-100 text-purple-700';
      case 'principal': return 'bg-blue-100 text-blue-700';
      case 'parent': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Filter rules
  const filteredRules = rules.filter(rule => {
    if (filterRole !== 'all' && rule.applies_to !== filterRole && rule.applies_to !== 'all') return false;
    if (filterCategory && rule.category !== filterCategory) return false;
    if (searchTerm && !rule.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !rule.description.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Group by category
  const groupedRules = filteredRules.reduce((acc, rule) => {
    const cat = rule.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(rule);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rule-book-page">
      
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Book className="w-7 h-7 text-sanjaya-navy" />
                Rule Book Management
              </h1>
              <p className="text-slate-600">Create and manage rules for all platform users</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadRules} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              {rules.length === 0 && (
                <Button variant="outline" onClick={initializeRules} className="border-amber-300 text-amber-600">
                  <Download className="w-4 h-4 mr-2" /> Load Default Rules
                </Button>
              )}
              <Button onClick={openCreateModal} className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark">
                <Plus className="w-4 h-4 mr-2" /> Add Rule
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium">Filters:</span>
                </div>
                
                <div className="flex-1 max-w-xs">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search rules..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                >
                  <option value="all">All Roles</option>
                  <option value="observer">Observers</option>
                  <option value="principal">Principals</option>
                  <option value="parent">Parents</option>
                </select>
                
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                
                <div className="text-sm text-slate-500">
                  {filteredRules.length} of {rules.length} rules
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rules List */}
          {rules.length === 0 ? (
            <Card className="p-12 text-center">
              <Book className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-slate-700 mb-2">No Rules Yet</h3>
              <p className="text-slate-500 mb-6">Start by adding rules or load the default rule set.</p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={initializeRules}>
                  <Download className="w-4 h-4 mr-2" /> Load Default Rules
                </Button>
                <Button onClick={openCreateModal} className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark">
                  <Plus className="w-4 h-4 mr-2" /> Create First Rule
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedRules).map(([category, categoryRules]) => (
                <Card key={category}>
                  <CardHeader className="pb-2 bg-slate-50 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="w-5 h-5 text-sanjaya-navy" />
                      {category}
                      <span className="text-sm font-normal text-slate-500">({categoryRules.length} rules)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {categoryRules.map((rule, idx) => (
                        <div 
                          key={rule.id} 
                          className={`p-4 ${!rule.is_active ? 'bg-slate-50 opacity-60' : ''}`}
                        >
                          <div 
                            className="flex items-start gap-4 cursor-pointer"
                            onClick={() => setExpandedRule(expandedRule === rule.id ? null : rule.id)}
                          >
                            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-700 font-bold text-sm">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-slate-800">{rule.title}</h4>
                                <span className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 ${getRoleBadgeColor(rule.applies_to)}`}>
                                  {getRoleIcon(rule.applies_to)}
                                  {rule.applies_to === 'all' ? 'Everyone' : rule.applies_to}
                                </span>
                                <span className={`px-2 py-0.5 rounded border text-xs ${getPriorityBadge(rule.priority)}`}>
                                  {rule.priority}
                                </span>
                                {!rule.is_active && (
                                  <span className="px-2 py-0.5 bg-gray-200 text-slate-600 rounded text-xs">Inactive</span>
                                )}
                              </div>
                              <p className="text-slate-600 text-sm">{rule.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditModal(rule); }}>
                                <Edit2 className="w-4 h-4 text-sanjaya-navy" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggleActive(rule); }}>
                                {rule.is_active ? <Eye className="w-4 h-4 text-green-600" /> : <Eye className="w-4 h-4 text-slate-400" />}
                              </Button>
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteRule(rule.id); }}>
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                              {expandedRule === rule.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                            </div>
                          </div>
                          
                          {expandedRule === rule.id && (
                            <div className="mt-4 ml-12 space-y-3 border-l-2 border-blue-200 pl-4">
                              {rule.consequences && (
                                <div>
                                  <p className="text-sm font-medium text-red-700 flex items-center gap-1">
                                    <AlertTriangle className="w-4 h-4" /> Consequences
                                  </p>
                                  <p className="text-sm text-slate-600">{rule.consequences}</p>
                                </div>
                              )}
                              {rule.examples && (
                                <div>
                                  <p className="text-sm font-medium text-green-700 flex items-center gap-1">
                                    <CheckCircle className="w-4 h-4" /> Examples
                                  </p>
                                  <p className="text-sm text-slate-600">{rule.examples}</p>
                                </div>
                              )}
                              <div className="text-xs text-slate-400">
                                Last updated: {rule.updated_at ? new Date(rule.updated_at).toLocaleDateString() : 'N/A'}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{editingRule ? 'Edit Rule' : 'Create New Rule'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-sm font-medium text-slate-700">Rule Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Session Punctuality"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  rows={3}
                  placeholder="Detailed description of the rule..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Category</label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    placeholder="e.g., Session Guidelines"
                    list="categories"
                  />
                  <datalist id="categories">
                    {categories.map(cat => <option key={cat} value={cat} />)}
                  </datalist>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700">Applies To</label>
                  <select
                    value={formData.applies_to}
                    onChange={(e) => setFormData({...formData, applies_to: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="all">Everyone</option>
                    <option value="observer">Observers Only</option>
                    <option value="principal">Principals Only</option>
                    <option value="parent">Parents Only</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700">Order</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                    min={0}
                  />
                </div>
                
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-slate-700">Active</label>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Consequences (if violated)</label>
                <textarea
                  value={formData.consequences}
                  onChange={(e) => setFormData({...formData, consequences: e.target.value})}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  rows={2}
                  placeholder="What happens if this rule is broken..."
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Examples / Guidelines</label>
                <textarea
                  value={formData.examples}
                  onChange={(e) => setFormData({...formData, examples: e.target.value})}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  rows={2}
                  placeholder="Practical examples of how to follow this rule..."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button onClick={saveRule} disabled={saving} className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark">
                  {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Rule</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PrincipalRuleBook;
